import React, {
  useRef,
  useEffect,
  useCallback,
  useContext,
  useState,
} from 'react';
import styled from 'styled-components';
import Cookie from 'universal-cookie';
import dateFormat from 'dateformat';
import { SocketContext } from '../common/SocketProvider';
import { ChatContext } from '../common/ChatProvider';

import verified from '../../svg/check-mark.svg';

const MAX_MESSAGES = 100;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  max-height: calc(100vh - 2rem);
  background-color: ${(p) => p.theme.color.white10};
`;

const Log = styled.div`
  width: 100%;
  flex: 1;
  overflow: auto;
  font-size: 0.8rem;
  line-height: 1.1rem;

  scrollbar-color: #aaa #0000;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    opacity: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const Row = styled.div`
  padding: 0.3rem;
  font-size: 1rem;
  ${(p) => p.error && `color: ${(p) => p.theme.color.turquoise100}`};
  display: flex;
  align-items: flex-start;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Verified = styled.img`
  width: 16px;
  margin: 0 0.3rem 0 0;
  filter: invert(1);
`;

const Timestamp = styled.div`
  white-space: nowrap;
  margin-right: 0.3rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: bold;
  opacity: 0.8;
`;

const RowText = styled.span`
  word-break: break-word;
`;

const RowUser = styled.span`
  white-space: pre;
  font-weight: bold;
  color: ${(p) => p.theme.color.boldText};

  &::after {
    content: ': ';
    color: ${(p) => p.theme.color.white100};
  }
`;

const Button = styled.button`
  background-color: ${(p) => p.color};
  width: 4rem;
`;

const Block = styled.span`
  display: block;
  word-break: break-all;
`;

const AdminShoutbox = () => {
  const connection = useContext(SocketContext);
  const logContext = useContext(ChatContext);
  const cookieRef = useRef(null);
  const chatLogRef = useRef(null);

  const [scrollToNewMessages, setScrollToNewMessages] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(true);

  const setMessageVerifyState = (id, verified) => {
    connection.send(
      JSON.stringify({
        messageId: id,
        verified: verified,
        type: 'verify',
      })
    );
  };

  const deleteMessage = (id, name) => {
    const confirm = confirmDelete
      ? window.confirm(`delete message by ${name}`)
      : true;

    if (!confirm) return;

    connection.send(
      JSON.stringify({
        messageId: id,
        type: 'delete',
      })
    );
  };

  const showError = useCallback(
    (evt) => {
      logContext.setLog([
        {
          error: true,
          name: 'SERVER',
          text: 'Huutislaatikko ei saa yhteyttä! :(',
          timestamp: new Date(),
        },
      ]);
    },
    [logContext]
  );

  const handleData = useCallback(
    (evt) => {
      const data = JSON.parse(evt.data);

      const chatLog = chatLogRef.current;
      const scroll = chatLog.scrollTop;
      let newMessageAdded = false;

      logContext.setLog((currentLog) => {
        if (data.initial) {
          // Set the initial log
          return data.initial;
        } else if (data.error) {
          // Handle error messages
          console.log('error');
          const errorLog = currentLog.concat([
            {
              error: true,
              name: 'SERVER',
              text: data.message,
              timestamp: new Date(),
            },
          ]);
          return errorLog;
        } else if (data.message) {
          // Check if the message already exists in the log
          const existingIndex = currentLog.findIndex(
            (m) => m._id === data.message._id
          );

          if (existingIndex !== -1) {
            // Replace the existing message with the updated one
            const updatedLog = [...currentLog];
            updatedLog[existingIndex] = {
              ...currentLog[existingIndex],
              ...data.message,
            };
            return updatedLog;
          } else {
            newMessageAdded = true;
            // Add the new message to the log
            const newLog = currentLog.concat([data.message]);
            if (newLog.length > MAX_MESSAGES) {
              newLog.splice(0, 1);
            }
            return newLog;
          }
        } else if (data.erase) {
          // Remove the message from the log
          const id = data.erase;
          const updatedLog = currentLog.filter((m) => m._id !== id);
          return updatedLog;
        }

        return currentLog; // Return the current log if no changes are made
      });
      if (newMessageAdded && scrollToNewMessages) {
        chatLog.scrollTop = chatLog.scrollHeight;
      } else if (newMessageAdded) {
        chatLog.scrollTop = scroll - 30; // very scuffed way to keep the scroll position :D
      }
    },
    [logContext, scrollToNewMessages]
  );

  const renderRows = (rows) => {
    // Filter out messages from before 2025
    rows = rows.filter(
      (row) =>
        new Date(Date.parse(row.timestamp)) >= new Date('2025-04-01T00:00:00Z')
    );
    return rows.map((row, index) => (
      <Row error={row.error} key={index}>
        {!row.error && (
          <React.Fragment>
            <Block>
              <Button
                color={row.verified ? '#64ff4d' : '#b3b3b3'}
                onClick={() => setMessageVerifyState(row._id, !row.verified)}
              >
                {row.verified ? 'verified' : 'verify'}
              </Button>
            </Block>
            <Block>
              <Button
                color="orange"
                onClick={() => deleteMessage(row._id, row.name)}
              >
                delete
              </Button>
            </Block>
          </React.Fragment>
        )}
        <Block>
          <Timestamp>
            {dateFormat(new Date(Date.parse(row.timestamp)), 'dd.mm HH:MM')}
          </Timestamp>
        </Block>
        <Block>
          {(row.reserved || row.admin) && <Verified src={verified} />}
          <RowUser>{row.name}</RowUser>
          <RowText>{row.text}</RowText>
        </Block>
      </Row>
    ));
  };

  useEffect(() => {
    connection.addEventListener('message', handleData);
    connection.addEventListener('error', showError);

    return () => {
      connection.removeEventListener('message', handleData);
      connection.removeEventListener('error', showError);
    };
  }, [connection, handleData, showError]);

  // fetch all initial messages
  useEffect(() => {
    const fetchMessages = () => {
      connection.send(
        JSON.stringify({
          type: 'fetch_messages',
        })
      );
    };

    if (connection.readyState === WebSocket.OPEN) {
      fetchMessages();
    } else {
      connection.onopen = () => {
        fetchMessages();
      };

      // Optional safety fallback
      const timeout = setTimeout(() => {
        if (connection.readyState === WebSocket.OPEN) {
          fetchMessages();
        }
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [connection]);

  useEffect(() => {
    cookieRef.current = new Cookie();
  }, []);

  return (
    <Container>
      <Log ref={chatLogRef}>{renderRows(logContext.log)}</Log>
      <h1>options</h1>
      <Row>
        <label htmlFor="confirm_scroll_to_new">
          Scroll to bottom on new message:
        </label>
        <input
          id="confirm_scroll_to_new"
          type="checkbox"
          checked={scrollToNewMessages}
          onChange={(e) => setScrollToNewMessages(e.target.checked)}
        />
      </Row>
      <Row>
        <label htmlFor="confirm_delete">Confirm delete:</label>
        <input
          id="confirm_delete"
          type="checkbox"
          checked={confirmDelete}
          onChange={(e) => setConfirmDelete(e.target.checked)}
        />
      </Row>
      <InputContainer></InputContainer>
    </Container>
  );
};

export default AdminShoutbox;
